import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, venueHeaders, setVenueToken, getVenueToken, formatApiError } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Copy, CreditCard, Gift, Loader2, LogOut, Printer, Radio, Trash2, Tv, Upload } from "lucide-react";

const EMPTY_SPECIAL = { title: "", description: "", tag: "FOOD", image_url: "" };

function AuthPanel({ plan, setPlan, onAuthed }) {
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", region: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit() {
    setBusy(true);
    try {
      if (mode === "register") {
        const { data } = await api.post("/auth/register", {
          role: plan, name: form.name, region: form.region, email: form.email, password: form.password,
        });
        setVenueToken(data.token);
        onAuthed(data.venue);
        toast.success(`${data.venue.name} is live — code ${data.venue.code}`);
      } else {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        setVenueToken(data.token);
        onAuthed(data.venue);
        toast.success(`Welcome back to ${data.venue.name}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  }

  return (
    <div className="glass w-full max-w-md rounded-3xl p-8" data-testid="venue-auth-card">
      <div className="mb-7 flex gap-2">
        {["register", "login"].map((m) => (
          <button
            key={m}
            data-testid={`venue-auth-tab-${m}`}
            onClick={() => setMode(m)}
            className={`flex-1 rounded-full py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.25em] transition-all ${mode === m ? "bg-amber text-navy-900" : "border border-ice/20 text-mut hover:text-cream"}`}
          >
            {m === "register" ? "Create room" : "Sign in"}
          </button>
        ))}
      </div>

      {mode === "register" && (
        <div className="mb-6 grid grid-cols-2 gap-3">
          {[
            { key: "venue", title: "Venue", price: "$29.99/mo" },
            { key: "home", title: "Home", price: "$9.99/mo" },
          ].map((p) => (
            <button
              key={p.key}
              data-testid={`venue-plan-${p.key}`}
              onClick={() => setPlan(p.key)}
              className={`rounded-2xl border-2 p-4 text-left transition-all ${plan === p.key ? "border-amber bg-amber/10" : "border-ice/15 bg-navy-800/60 hover:border-ice/40"}`}
            >
              <div className="font-display text-lg font-extrabold uppercase text-cream">{p.title}</div>
              <div className="font-mono text-xs text-amber-soft">{p.price}</div>
            </button>
          ))}
        </div>
      )}

      <div className="space-y-4">
        {mode === "register" && (
          <input data-testid="venue-name-input" value={form.name} onChange={set("name")} placeholder={plan === "home" ? "Room name (e.g. The Brain Lounge)" : "Bar name (e.g. Ugly Joe's)"} className="w-full rounded-2xl border-2 border-ice/20 bg-navy-800/80 px-5 py-3.5 font-bold text-cream outline-none focus:border-amber" />
        )}
        {mode === "register" && (
          <input data-testid="venue-region-input" value={form.region} onChange={set("region")} placeholder="City / metro (e.g. Gardner, KS)" className="w-full rounded-2xl border-2 border-ice/20 bg-navy-800/80 px-5 py-3.5 text-cream outline-none focus:border-amber" />
        )}
        <input data-testid="venue-email-input" value={form.email} onChange={set("email")} type="email" placeholder="Email" className="w-full rounded-2xl border-2 border-ice/20 bg-navy-800/80 px-5 py-3.5 text-cream outline-none focus:border-amber" />
        <input data-testid="venue-password-input" value={form.password} onChange={set("password")} type="password" placeholder="Password (6+ characters)" className="w-full rounded-2xl border-2 border-ice/20 bg-navy-800/80 px-5 py-3.5 text-cream outline-none focus:border-amber" />
        <button
          data-testid="venue-auth-submit"
          onClick={submit}
          disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-amber py-4 font-display font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_36px_rgba(245,179,53,0.4)] disabled:opacity-60"
        >
          {busy ? <Loader2 size={18} className="animate-spin" /> : mode === "register" ? `Open my ${plan === "home" ? "home room" : "venue"} — free while in season zero` : "Sign in"}
        </button>
      </div>
    </div>
  );
}


export default function VenueDashboard() {
  const [params, setParams] = useSearchParams();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(params.get("plan") === "home" ? "home" : "venue");
  const [settings, setSettings] = useState({ name: "", region: "", logo_url: "", ticker_speed: 42 });
  const [specials, setSpecials] = useState([]);
  const [newSpecial, setNewSpecial] = useState(EMPTY_SPECIAL);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const galleryRef = useRef(null);
  const [galleryUrl, setGalleryUrl] = useState("");
  const [loyalty, setLoyalty] = useState({ enabled: false, trigger: "games", title: "", description: "", games_required: 3 });

  useEffect(() => {
    if (params.get("plan")) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadVenue() {
    if (!getVenueToken()) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/venue/me", { headers: venueHeaders() });
      applyVenue(data.venue);
    } catch (e) {
      setVenueToken("");
    } finally {
      setLoading(false);
    }
  }

  function applyVenue(v) {
    setVenue(v);
    setSettings({ name: v.name, region: v.region, logo_url: v.logo_url || "", ticker_speed: v.ticker_speed || 42 });
    setSpecials(v.specials || []);
    const lo = v.loyalty_offer;
    setLoyalty(lo && lo.title
      ? { enabled: !!lo.enabled, trigger: lo.trigger || "games", title: lo.title, description: lo.description || "", games_required: lo.games_required || 3 }
      : { enabled: false, trigger: "games", title: "", description: "", games_required: 3 });
  }

  useEffect(() => {
    loadVenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveSettings() {
    try {
      const { data } = await api.put("/venue/me", { ...settings, logo_url: settings.logo_url || null, ticker_speed: Number(settings.ticker_speed) || 42 }, { headers: venueHeaders() });
      applyVenue(data.venue);
      toast.success("Room settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function saveSpecials(next) {
    try {
      const { data } = await api.put("/venue/me", { specials: next, ticker_speed: Number(settings.ticker_speed) || 42 }, { headers: venueHeaders() });
      applyVenue(data.venue);
      toast.success("Specials updated — live on the big screen within seconds");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function addSpecial() {
    if (newSpecial.title.trim().length < 2) {
      toast.error("Give the special a name");
      return;
    }
    saveSpecials([...specials, { ...newSpecial, title: newSpecial.title.trim() }]);
    setNewSpecial(EMPTY_SPECIAL);
  }

  async function uploadImage(file) {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: venueHeaders() });
      setNewSpecial((s) => ({ ...s, image_url: data.url }));
      toast.success("Photo uploaded — it will scroll across the big screen");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  }

  async function uploadToStorage(file) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads", fd, { headers: venueHeaders() });
    return data.url;
  }

  async function uploadLogo(file) {
    setUploading(true);
    try {
      const url = await uploadToStorage(file);
      const { data } = await api.put("/venue/me", { logo_url: url }, { headers: venueHeaders() });
      applyVenue(data.venue);
      toast.success("Logo uploaded — it's live on the big screen");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  }

  async function uploadGalleryImage(file) {
    setUploading(true);
    try {
      const url = await uploadToStorage(file);
      await saveGallery([...(venue.gallery || []), { image_url: url, caption: "" }]);
    } catch (e) {
      toast.error(formatApiError(e));
      setUploading(false);
    }
  }

  async function saveGallery(next) {
    try {
      const { data } = await api.put("/venue/me", { gallery: next }, { headers: venueHeaders() });
      applyVenue(data.venue);
      toast.success("Intermission photos updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  }

  async function saveLoyalty() {
    if (loyalty.enabled && loyalty.title.trim().length < 2) {
      toast.error("Give the reward a name (e.g. Free appetizer)");
      return;
    }
    try {
      const { data } = await api.put("/venue/me", {
        loyalty_offer: { ...loyalty, title: loyalty.title.trim(), games_required: Number(loyalty.games_required) || 3 },
      }, { headers: venueHeaders() });
      applyVenue(data.venue);
      toast.success(loyalty.enabled ? "Reward is live — it pops up on players' phones" : "Reward turned off");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }


  async function checkout(lookupKey) {
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin, user_id: venue.id }, { headers: venueHeaders() });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-navy-900 font-mono text-sm uppercase tracking-[0.3em] text-mut">Loading your room…</div>;
  }

  if (!venue) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-8 bg-navy-900 px-4 py-10 text-cream"
        style={{ background: "radial-gradient(circle at 50% 0%, rgba(120,80,255,0.15), transparent 55%), #190a20" }}
        data-testid="venue-auth-page"
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="/brand/braintap-logo-full.png" alt="braintap.bar" className="h-20 w-auto object-contain" />
            <span className="font-display text-xl font-extrabold tracking-tight">Sign up or log in</span>
          </Link>
          <p className="max-w-md text-sm text-mut">New here? Pick Home or Venue below. Already have a room? Use the Sign in tab.</p>
        </div>
        <AuthPanel plan={plan} setPlan={setPlan} onAuthed={(v) => applyVenue(v)} />
      </div>
    );
  }

  const joinUrl = `${window.location.origin}/join?code=${venue.code}`;

  return (
    <div className="min-h-screen bg-navy-900 px-5 py-8 text-cream" data-testid="venue-dashboard">
      <div className="mx-auto max-w-6xl">
        <header className="mb-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {venue.logo_url ? (
              <img src={venue.logo_url} alt={venue.name} className="h-16 max-w-[180px] object-contain" data-testid="venue-header-logo" />
            ) : (
              <LogoMark className="h-11 w-11" />
            )}
            <div>
              <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight" data-testid="venue-title">{venue.name}</h1>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-mut">
                {venue.plan === "home" ? "Home room" : "Venue"} · {venue.region || "No region set"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <img src="/brand/braintap-logo-full.png" alt="braintap.bar" className="hidden lg:block h-16 w-auto object-contain" data-testid="venue-header-brand" />
            <Link data-testid="venue-open-tv" to={`/tv/${venue.code}`} target="_blank" className="inline-flex items-center gap-2 rounded-full border-2 border-ice/25 px-5 py-2.5 text-sm font-bold transition-all hover:border-amber hover:text-amber">
              <Tv size={16} /> Open big screen
            </Link>
            <button
              data-testid="venue-logout"
              onClick={() => { setVenueToken(""); setVenue(null); }}
              className="inline-flex items-center gap-2 rounded-full border border-ice/20 px-5 py-2.5 text-sm font-bold text-mut transition-all hover:border-coral hover:text-coral"
            >
              <LogOut size={15} /> Log out
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="card-navy rounded-3xl p-8 lg:col-span-1" data-testid="venue-qr-card">
            <h2 className="mb-5 font-display text-lg font-extrabold uppercase tracking-wide text-amber">Your permanent QR</h2>
            <div className="mx-auto w-fit rounded-2xl bg-white p-4">
              <QRCodeSVG value={joinUrl} size={150} fgColor="#190a20" />
            </div>
            <button
              data-testid="venue-copy-link"
              onClick={() => { navigator.clipboard.writeText(joinUrl); toast.success("Join link copied"); }}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-full border border-ice/25 py-3 text-sm font-bold transition-all hover:border-amber hover:text-amber"
            >
              <Copy size={15} /> Copy join link
            </button>
            <a
              data-testid="venue-poster-link"
              href={`/poster/${venue.code}`}
              target="_blank"
              rel="noreferrer"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-ice/25 py-3 text-sm font-bold transition-all hover:border-amber hover:text-amber"
            >
              <Printer size={15} /> Print join poster
            </a>
          </div>

          <div className="card-navy rounded-3xl p-8" data-testid="venue-settings-card">
            <h2 className="mb-5 font-display text-lg font-extrabold uppercase tracking-wide text-amber">Room settings</h2>
            <div className="space-y-4">
              <input data-testid="settings-name-input" value={settings.name} onChange={(e) => setSettings({ ...settings, name: e.target.value })} placeholder="Room name" className="w-full rounded-2xl border-2 border-ice/20 bg-navy-900/70 px-5 py-3 font-bold text-cream outline-none focus:border-amber" />
              <input data-testid="settings-region-input" value={settings.region} onChange={(e) => setSettings({ ...settings, region: e.target.value })} placeholder="City / metro (e.g. Gardner, KS)" className="w-full rounded-2xl border-2 border-ice/20 bg-navy-900/70 px-5 py-3 text-cream outline-none focus:border-amber" />
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-mut" data-testid="metro-hint">
                Leaderboard area: {venue.metro || "not set"} · nearby cities share one board (Gardner, KS joins the Kansas City Metro)
              </div>
              <input data-testid="settings-logo-input" value={settings.logo_url} onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })} placeholder="Logo image URL (optional)" className="w-full rounded-2xl border-2 border-ice/20 bg-navy-900/70 px-5 py-3 text-cream outline-none focus:border-amber" />
              <div className="space-y-3">
                <input ref={logoRef} data-testid="venue-logo-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => { if (e.target.files && e.target.files[0]) uploadLogo(e.target.files[0]); e.target.value = ""; }} />
                {venue.logo_url && <img src={venue.logo_url} alt="logo" data-testid="venue-logo-preview" className="mx-auto block h-36 w-full rounded-2xl bg-white/5 object-contain p-3 ring-1 ring-ice/20" />}
                <button data-testid="venue-logo-upload" onClick={() => logoRef.current && logoRef.current.click()} disabled={uploading} className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-dashed border-ice/30 px-5 py-2.5 text-sm font-bold text-mut transition-colors hover:border-amber hover:text-amber disabled:opacity-60">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload logo file
                </button>
              </div>
              <button data-testid="settings-save" onClick={saveSettings} className="w-full rounded-full bg-amber py-3.5 font-display font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5">
                Save settings
              </button>
            </div>
          </div>

          <div className="card-navy rounded-3xl p-8" data-testid="venue-billing-card">
            <h2 className="mb-5 flex items-center gap-2 font-display text-lg font-extrabold uppercase tracking-wide text-amber">
              <CreditCard size={17} /> Plan &amp; billing
            </h2>
            <div className="mb-5 flex items-center justify-between rounded-2xl bg-navy-900/70 px-5 py-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-mut">Status</span>
              <span data-testid="venue-billing-status" className={`rounded-full px-4 py-1 font-mono text-xs font-bold uppercase tracking-[0.2em] ${venue.subscription_status === "active" ? "bg-mint/20 text-mint" : "bg-amber/15 text-amber-soft"}`}>
                {venue.subscription_status === "active" ? "Subscribed" : "Free trial"}
              </span>
            </div>
            <p className="mb-6 text-sm leading-relaxed text-mut">Subscriptions bill monthly through Stripe. Cancel anytime.</p>
            <div className="space-y-3">
              <button data-testid="billing-venue-button" onClick={() => checkout("venue_monthly")} className="w-full rounded-full bg-amber py-3.5 font-display font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5">
                Venue — $29.99/mo
              </button>
              <button data-testid="billing-home-button" onClick={() => checkout("home_monthly")} className="w-full rounded-full border-2 border-ice/25 py-3.5 font-display font-extrabold uppercase tracking-wide text-cream transition-all hover:border-amber hover:text-amber">
                Home — $9.99/mo
              </button>
            </div>
            <a
              href="https://dashboard.stripe.com/onboard_sandbox/YWNjdF8xVVg3VnBEeHJzeERjbFpJLDE3OTQxODY3NDQv100YYpykgCb"
              target="_blank"
              rel="noreferrer"
              data-testid="stripe-claim-link"
              className="mt-5 block text-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#c9a3ff] underline decoration-dotted"
            >
              Claim your Stripe account to take real payments
            </a>
          </div>

          <div className="card-navy rounded-3xl p-8" data-testid="venue-loyalty-card">
            <h2 className="mb-2 flex items-center gap-2 font-display text-lg font-extrabold uppercase tracking-wide text-amber">
              <Gift size={17} /> Player reward
            </h2>
            <p className="mb-5 text-sm text-mut">Optional — reward your regulars: play enough games here and a coupon pops up on their phone to show your bartender.</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2" data-testid="loyalty-trigger-picker">
                <button
                  data-testid="loyalty-trigger-games"
                  onClick={() => setLoyalty({ ...loyalty, trigger: "games" })}
                  className={`rounded-2xl px-4 py-3 font-display text-sm font-extrabold uppercase tracking-wide transition-all ${loyalty.trigger !== "wins" ? "bg-mint/20 text-mint ring-2 ring-mint/50" : "border-2 border-ice/20 text-mut hover:border-amber hover:text-amber"}`}
                >
                  Games played
                </button>
                <button
                  data-testid="loyalty-trigger-wins"
                  onClick={() => setLoyalty({ ...loyalty, trigger: "wins" })}
                  className={`rounded-2xl px-4 py-3 font-display text-sm font-extrabold uppercase tracking-wide transition-all ${loyalty.trigger === "wins" ? "bg-mint/20 text-mint ring-2 ring-mint/50" : "border-2 border-ice/20 text-mut hover:border-amber hover:text-amber"}`}
                >
                  First-place wins
                </button>
              </div>
              <button
                data-testid="loyalty-toggle"
                onClick={() => setLoyalty({ ...loyalty, enabled: !loyalty.enabled })}
                className={`flex w-full items-center justify-between rounded-2xl px-5 py-4 font-display text-sm font-extrabold uppercase tracking-wide transition-all ${loyalty.enabled ? "bg-mint/20 text-mint" : "border-2 border-ice/20 text-mut hover:border-amber hover:text-amber"}`}
              >
                {loyalty.enabled ? "Reward is ON" : "Reward is OFF"}
                <span className={`flex h-6 w-11 items-center rounded-full p-1 transition-colors ${loyalty.enabled ? "justify-end bg-mint" : "justify-start bg-navy-700"}`}>
                  <span className="block h-4 w-4 rounded-full bg-white" />
                </span>
              </button>
              <input data-testid="loyalty-title-input" value={loyalty.title} onChange={(e) => setLoyalty({ ...loyalty, title: e.target.value })} placeholder="Reward name (e.g. Free appetizer)" className="w-full rounded-2xl border-2 border-ice/20 bg-navy-900/70 px-5 py-3 font-bold text-cream outline-none focus:border-amber" />
              <input data-testid="loyalty-desc-input" value={loyalty.description} onChange={(e) => setLoyalty({ ...loyalty, description: e.target.value })} placeholder="Details (e.g. Chips + queso, on the house)" className="w-full rounded-2xl border-2 border-ice/20 bg-navy-900/70 px-5 py-3 text-cream outline-none focus:border-amber" />
              <div className="flex items-center gap-3">
                <input data-testid="loyalty-games-input" type="number" min={1} max={100} value={loyalty.games_required} onChange={(e) => setLoyalty({ ...loyalty, games_required: e.target.value })} className="w-24 rounded-2xl border-2 border-ice/20 bg-navy-900/70 px-5 py-3 font-mono-num font-bold text-cream outline-none focus:border-amber" />
                <span className="text-sm text-mut">{loyalty.trigger === "wins" ? "first-place wins here, then the reward unlocks" : "games played here, then the reward unlocks"}</span>
              </div>
              <button data-testid="loyalty-save" onClick={saveLoyalty} className="w-full rounded-full bg-amber py-3.5 font-display font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5">
                Save reward
              </button>
            </div>
          </div>

          <div className="card-navy rounded-3xl p-8 md:col-span-2" data-testid="venue-gallery-card">
            <h2 className="mb-2 font-display text-lg font-extrabold uppercase tracking-wide text-amber">Intermission photos</h2>
            <p className="mb-6 text-sm text-mut">Big pictures shown between games — menu shots, drink deals, whatever you want on screen. These also join the scrolling ticker.</p>
            <div className="mb-6 flex flex-wrap items-center gap-4">
              <input ref={galleryRef} data-testid="gallery-photo-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => { if (e.target.files && e.target.files[0]) uploadGalleryImage(e.target.files[0]); e.target.value = ""; }} />
              <button data-testid="gallery-photo-button" onClick={() => galleryRef.current && galleryRef.current.click()} disabled={uploading} className="flex items-center gap-2 rounded-2xl border-2 border-dashed border-ice/30 px-5 py-3 text-sm font-bold text-mut transition-colors hover:border-amber hover:text-amber disabled:opacity-60">
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Add photo
              </button>
              <input data-testid="gallery-url-input" value={galleryUrl} onChange={(e) => setGalleryUrl(e.target.value)} placeholder="…or paste an image URL" className="min-w-[220px] flex-1 rounded-2xl border-2 border-ice/20 bg-navy-900/70 px-4 py-3 text-sm text-cream outline-none focus:border-amber" />
              <button
                data-testid="gallery-add"
                onClick={() => {
                  if (!galleryUrl.trim()) { toast.error("Paste an image URL first"); return; }
                  saveGallery([...(venue.gallery || []), { image_url: galleryUrl.trim(), caption: "" }]);
                  setGalleryUrl("");
                }}
                className="rounded-2xl bg-amber px-8 py-3 font-display font-extrabold uppercase text-navy-900 transition-all hover:-translate-y-0.5"
              >
                Add
              </button>
            </div>
            {(venue.gallery || []).length === 0 ? (
              <div className="rounded-2xl border border-dashed border-ice/25 px-6 py-8 text-center text-mut">No intermission photos yet — add your menu and drink shots and they'll rotate full-size between games.</div>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {venue.gallery.map((g, i) => (
                  <div key={i} className="group relative overflow-hidden rounded-2xl border border-ice/15" data-testid={`gallery-item-${i}`}>
                    <div className="relative">
                      <img src={g.image_url || g} alt="" className="h-28 w-full object-cover" />
                      <button data-testid={`gallery-delete-${i}`} onClick={() => saveGallery(venue.gallery.filter((_, j) => j !== i))} className="absolute right-2 top-2 rounded-full bg-navy-900/80 p-1.5 text-mut opacity-0 transition-opacity hover:text-coral group-hover:opacity-100" aria-label="delete photo">
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="space-y-2 bg-navy-900/60 p-3">
                      <input key={`${g.image_url}-${i}`} data-testid={`gallery-caption-${i}`} defaultValue={g.caption || ""} onBlur={(e) => saveGallery(venue.gallery.map((x, j) => j === i ? { ...x, caption: e.target.value } : x))} placeholder="Label (e.g. Trivia Tuesdays!)" className="w-full rounded-xl border-2 border-ice/20 bg-navy-900/70 px-3 py-2 text-xs text-cream outline-none focus:border-amber" />
                      <div className="flex gap-2">
                        <select data-testid={`gallery-font-${i}`} value={g.caption_font || "baloo"} onChange={(e) => saveGallery(venue.gallery.map((x, j) => j === i ? { ...x, caption_font: e.target.value } : x))} className="min-w-0 flex-1 rounded-xl border-2 border-ice/20 bg-navy-900/70 px-2 py-2 text-xs text-cream outline-none focus:border-amber" aria-label="Caption font">
                          <option value="baloo">Baloo</option>
                          <option value="bricolage">Bricolage</option>
                          <option value="mono">Mono</option>
                          <option value="serif">Serif</option>
                        </select>
                        <select data-testid={`gallery-pos-${i}`} value={g.caption_pos || "bottom"} onChange={(e) => saveGallery(venue.gallery.map((x, j) => j === i ? { ...x, caption_pos: e.target.value } : x))} className="min-w-0 flex-1 rounded-xl border-2 border-ice/20 bg-navy-900/70 px-2 py-2 text-xs text-cream outline-none focus:border-amber" aria-label="Caption position">
                          <option value="top">Top</option>
                          <option value="center">Center</option>
                          <option value="bottom">Bottom</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card-navy rounded-3xl p-8 lg:col-span-3" data-testid="venue-specials-card">
            <h2 className="mb-2 font-display text-lg font-extrabold uppercase tracking-wide text-amber">Food &amp; drink specials</h2>
            <p className="mb-6 text-sm text-mut">These rotate at all times — during the game and intermission.</p>
            <div className="mb-4 flex flex-wrap items-center gap-4">
              <input
                ref={fileRef}
                data-testid="special-photo-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(e) => { if (e.target.files && e.target.files[0]) uploadImage(e.target.files[0]); e.target.value = ""; }}
              />
              <button
                data-testid="special-photo-button"
                onClick={() => fileRef.current && fileRef.current.click()}
                disabled={uploading}
                className="flex items-center gap-2 rounded-2xl border-2 border-dashed border-ice/30 px-5 py-3 text-sm font-bold text-mut transition-colors hover:border-amber hover:text-amber disabled:opacity-60"
              >
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                {newSpecial.image_url ? "Swap photo" : "Add photo"}
              </button>
              {newSpecial.image_url && (
                <img src={newSpecial.image_url} alt="" data-testid="special-photo-preview" className="h-12 w-16 rounded-lg object-cover ring-1 ring-ice/25" />
              )}
              <input
                data-testid="special-image-input"
                value={newSpecial.image_url}
                onChange={(e) => setNewSpecial({ ...newSpecial, image_url: e.target.value })}
                placeholder="…or paste a photo URL"
                className="min-w-[220px] flex-1 rounded-2xl border-2 border-ice/20 bg-navy-900/70 px-4 py-3 text-sm text-cream outline-none focus:border-amber"
              />
            </div>
            <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-[1fr_2fr_auto_auto]">
              <input data-testid="special-title-input" value={newSpecial.title} onChange={(e) => setNewSpecial({ ...newSpecial, title: e.target.value })} placeholder="Wings" className="rounded-2xl border-2 border-ice/20 bg-navy-900/70 px-4 py-3 font-bold text-cream outline-none focus:border-amber" />
              <input data-testid="special-desc-input" value={newSpecial.description} onChange={(e) => setNewSpecial({ ...newSpecial, description: e.target.value })} placeholder="6 wings, any sauce, $8." className="rounded-2xl border-2 border-ice/20 bg-navy-900/70 px-4 py-3 text-cream outline-none focus:border-amber" />
              <select data-testid="special-tag-select" value={newSpecial.tag} onChange={(e) => setNewSpecial({ ...newSpecial, tag: e.target.value })} className="rounded-2xl border-2 border-ice/20 bg-navy-900/70 px-4 py-3 font-bold text-cream outline-none focus:border-amber">
                {["FOOD", "DRINK", "DEAL"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <button data-testid="special-add" onClick={addSpecial} className="rounded-2xl bg-amber px-8 font-display font-extrabold uppercase text-navy-900 transition-all hover:-translate-y-0.5">Add</button>
            </div>
            {specials.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-ice/25 px-6 py-8 text-center text-mut">No specials yet — add your first deal above and it hits the screen during the next break.</div>
            ) : (
              <div className="space-y-3">
                {specials.map((s, i) => (
                  <div key={i} className="flex items-center gap-4 rounded-2xl border border-ice/15 bg-navy-900/60 px-5 py-4" data-testid={`special-row-${i}`}>
                    <span className={`rounded-full px-3 py-1 font-mono text-[10px] font-bold tracking-[0.2em] ${s.tag === "DRINK" ? "bg-[#7c50ff]/30 text-[#cdb6ff]" : s.tag === "FOOD" ? "bg-amber/25 text-amber-soft" : "bg-mint/20 text-mint"}`}>{s.tag}</span>
                    {s.image_url && <img src={s.image_url} alt="" className="h-11 w-16 rounded-lg object-cover ring-1 ring-ice/20" />}
                    <div className="flex-1">
                      <div className="font-bold">{s.title}</div>
                      <div className="text-sm text-mut">{s.description}</div>
                    </div>
                    <button data-testid={`special-delete-${i}`} onClick={() => saveSpecials(specials.filter((_, j) => j !== i))} className="rounded-full p-2 text-mut transition-colors hover:bg-coral/15 hover:text-coral" aria-label="delete special">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
              <div className="mt-6 rounded-2xl border border-ice/15 bg-navy-900/60 px-5 py-4" data-testid="ticker-speed-control">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-cream">Ticker speed</span>
                  <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-amber-soft" data-testid="ticker-speed-label">
                    {Number(settings.ticker_speed) <= 25 ? "Fast" : Number(settings.ticker_speed) <= 55 ? "Normal" : "Slow & relaxed"}
                  </span>
                </div>
                <input data-testid="ticker-speed-slider" type="range" min={10} max={90} step={5} value={settings.ticker_speed} onChange={(e) => setSettings({ ...settings, ticker_speed: Number(e.target.value) })} className="mt-3 w-full accent-amber" />
                <div className="mt-1 flex justify-between font-mono text-[9px] uppercase tracking-[0.2em] text-mut"><span>Fast</span><span>Slow</span></div>
                <button data-testid="ticker-speed-save" onClick={() => saveSpecials(specials)} className="mt-3 w-full rounded-full bg-amber py-2.5 font-display text-sm font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5">
                  Save ticker speed
                </button>
              </div>
          </div>
        </div>
      </div>
    </div>
  );
}
